import React from 'react';
import { useTranslation } from '../context/TranslationContext';
import { formatCurrency } from '../utils/currencyUtils';
import '../styles/PreviewModal.css';

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  updatedSuppliers: any[];
  onApproveRequest: () => void;
  onBackToEdit: () => void;
}

const PreviewModal: React.FC<PreviewModalProps> = ({ 
  isOpen, 
  onClose, 
  updatedSuppliers,
  onApproveRequest,
  onBackToEdit
}) => {
  const { t } = useTranslation();

  console.log('🔍 DEBUG - PreviewModal - isOpen:', isOpen);
  console.log('🔍 DEBUG - PreviewModal - updatedSuppliers:', updatedSuppliers);
  console.log('🔍 DEBUG - PreviewModal - updatedSuppliers.length:', updatedSuppliers?.length);

  if (!isOpen || !updatedSuppliers?.length) {
    console.log('🔍 DEBUG - PreviewModal - Returning null because:', { isOpen, suppliersLength: updatedSuppliers?.length });
    return null;
  }

  // Debug supplier data structure
  console.log('🔍 DEBUG - PreviewModal - First supplier structure:', updatedSuppliers[0]);
  console.log('🔍 DEBUG - PreviewModal - First supplier keys:', Object.keys(updatedSuppliers[0] || {}));
  console.log('🔍 DEBUG - PreviewModal - First supplier products array:', updatedSuppliers[0]?.products);
  console.log('🔍 DEBUG - PreviewModal - First supplier first product:', updatedSuppliers[0]?.products?.[0]);
  console.log('🔍 DEBUG - PreviewModal - First supplier attached_documents:', updatedSuppliers[0]?.attached_documents);
  
  // Debug all suppliers for attached_documents
  updatedSuppliers.forEach((supplier, index) => {
    console.log(`🔍 DEBUG - PreviewModal - Supplier ${index} (${supplier.company_name}) attached_documents:`, supplier.attached_documents);
  });


  const comparisonData = [
    {
      label: t('common.supplier_name'),
      key: 'supplier_name',
      getValue: (supplier: any) => supplier.supplier_name || supplier.company_name || supplier.name,
      type: 'text'
    },
    {
      label: t('common.product_name'),
      key: 'product_name',
      getValue: (supplier: any) => {
        // Selected suppliers (after approval) have direct fields
        if (supplier.product_name) return supplier.product_name;
        // AI suppliers have products array
        if (supplier.products?.[0]?.name) return supplier.products[0].name;
        // Manual suppliers might have different structure
        if (supplier.products?.[0]?.name) return supplier.products[0].name;
        return 'N/A';
      },
      type: 'text'
    },
    {
      label: t('common.product_brand'),
      key: 'product_brand',
      getValue: (supplier: any) => {
        // Selected suppliers (after approval) have direct fields
        if (supplier.product_brand) return supplier.product_brand;
        // AI suppliers have products array
        if (supplier.products?.[0]?.brand) return supplier.products[0].brand;
        return 'N/A';
      },
      type: 'text'
    },
    {
      label: t('common.product_global_code'),
      key: 'product_global_code',
      getValue: (supplier: any) => supplier.product_global_code || 'N/A',
      type: 'text'
    },
    {
      label: t('common.product_company_code'),
      key: 'product_company_code',
      getValue: (supplier: any) => supplier.product_company_code || 'N/A',
      type: 'text'
    },
    {
      label: t('common.product_description'),
      key: 'product_description',
      getValue: (supplier: any) => {
        // Selected suppliers (after approval) have direct fields
        if (supplier.product_description) return supplier.product_description;
        // AI suppliers have products array
        if (supplier.products?.[0]?.description) return supplier.products[0].description;
        return 'N/A';
      },
      type: 'text'
    },
    {
      label: t('common.price'),
      key: 'price',
      getValue: (supplier: any) => {
        let price = 0;
        let currency = 'CHF';
        
        // Selected suppliers (after approval) have direct fields
        if (supplier.price || supplier.unit_price) {
          price = supplier.price || supplier.unit_price;
          let rawCurrency = supplier.currency || 'CHF';
          
          // Map common currency abbreviations
          const currencyMap: { [key: string]: string } = {
            'TL': 'TRY',
            'USD': 'USD',
            'EUR': 'EUR',
            'CHF': 'CHF',
            'GBP': 'GBP'
          };
          
          currency = currencyMap[rawCurrency] || rawCurrency;
        }
        // AI suppliers have products array
        else if (supplier.products?.[0]?.price) {
          const priceValue = supplier.products[0].price;
          
          console.log('🔍 DEBUG - PreviewModal - AI Supplier price parsing:', {
            supplier: supplier.company_name,
            originalPriceValue: priceValue,
            priceValueType: typeof priceValue,
            priceValueString: String(priceValue)
          });
          
          // Handle both string ("149 TL") and number (16148) price formats
          if (typeof priceValue === 'string') {
            // Extract numeric value from price string like "149 TL" or "11,780" or "11.208"
            // Try multiple parsing strategies
            let cleanedPrice = priceValue.trim();
            
            // Remove currency symbols and text
            cleanedPrice = cleanedPrice.replace(/[^\d.,]/g, '');
            
            // Special handling for Turkish number format
            // If string contains only digits and one dot (like "11.208"), treat as thousands separator
            if (/^\d+\.\d{3}$/.test(cleanedPrice)) {
              // Format like "11.208" -> treat as thousands separator (11,208)
              cleanedPrice = cleanedPrice.replace('.', '');
              console.log('🔍 DEBUG - PreviewModal - Detected thousands separator format:', priceValue, '->', cleanedPrice);
            }
            // Handle different decimal separators
            else if (cleanedPrice.includes(',') && cleanedPrice.includes('.')) {
              // Both comma and dot - assume comma is thousands separator, dot is decimal
              cleanedPrice = cleanedPrice.replace(/,/g, '');
            } else if (cleanedPrice.includes(',')) {
              // Only comma - could be decimal separator or thousands separator
              // If more than 3 digits after comma, treat as thousands separator
              const parts = cleanedPrice.split(',');
              if (parts.length === 2 && parts[1].length > 3) {
                cleanedPrice = parts.join('');
              } else {
                cleanedPrice = cleanedPrice.replace(',', '.');
              }
            }
            
            console.log('🔍 DEBUG - PreviewModal - Cleaned price string:', cleanedPrice);
            
            const numericPrice = parseFloat(cleanedPrice);
            console.log('🔍 DEBUG - PreviewModal - Parsed numeric price:', numericPrice);
            
            price = isNaN(numericPrice) ? 0 : numericPrice;
          } else if (typeof priceValue === 'number') {
            // Direct number value (from edit page)
            price = priceValue;
          }
          
          // For manual suppliers, check supplier.currency first, then products[0].currency
          let rawCurrency = supplier.currency || supplier.products[0].currency || 'CHF';
          
          // Map common currency abbreviations
          const currencyMap: { [key: string]: string } = {
            'TL': 'TRY',
            'USD': 'USD',
            'EUR': 'EUR',
            'CHF': 'CHF',
            'GBP': 'GBP'
          };
          
          currency = currencyMap[rawCurrency] || rawCurrency;
        }
        
        console.log('🔍 DEBUG - PreviewModal - Price calculation:', {
          supplier: supplier.company_name,
          originalPrice: supplier.products?.[0]?.price,
          extractedPrice: price,
          currency: currency,
          supplierCurrency: supplier.currency,
          productCurrency: supplier.products?.[0]?.currency,
          currencyMapping: `${supplier.currency || supplier.products?.[0]?.currency} → ${currency}`
        });
        
        return formatCurrency(price, currency, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
      type: 'currency'
    },
    {
      label: t('common.currency'),
      key: 'currency',
      getValue: (supplier: any) => {
        let rawCurrency = '';
        
        // Selected suppliers (after approval) have direct fields
        if (supplier.currency) {
          rawCurrency = supplier.currency;
        }
        // AI suppliers have products array
        else if (supplier.products?.[0]?.currency) {
          rawCurrency = supplier.products[0].currency;
        } else {
          return 'CHF';
        }
        
        // Map common currency abbreviations
        const currencyMap: { [key: string]: string } = {
          'TL': 'TRY',
          'USD': 'USD',
          'EUR': 'EUR',
          'CHF': 'CHF',
          'GBP': 'GBP'
        };
        
        return currencyMap[rawCurrency] || rawCurrency;
      },
      type: 'text'
    },
    {
      label: t('common.delivery_time'),
      key: 'delivery_time',
      getValue: (supplier: any) => supplier.delivery_time || 'N/A',
      type: 'text'
    },
    {
      label: t('common.minimum_order'),
      key: 'minimum_order',
      getValue: (supplier: any) => supplier.minimum_order || 'N/A',
      type: 'text'
    },
    {
      label: t('common.payment_terms'),
      key: 'payment_terms',
      getValue: (supplier: any) => supplier.payment_terms || 'N/A',
      type: 'text'
    },
    {
      label: t('common.notes'),
      key: 'notes',
      getValue: (supplier: any) => supplier.notes || 'N/A',
      type: 'text'
    },
    {
      label: t('common.attached_documents'),
      key: 'attached_documents',
      getValue: (supplier: any) => {
        const docs = supplier.attached_documents || [];
        console.log(`🔍 DEBUG - PreviewModal - getValue attached_documents for ${supplier.company_name}:`, docs);
        return docs;
      },
      type: 'documents'
    }
  ];

  return (
    <div className="preview-modal-overlay" onClick={onClose}>
      <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="preview-modal-close" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <div className="preview-modal-header">
          <h2>{t('common.preview_updated_products')}</h2>
          <p className="preview-modal-subtitle">
            {t('common.review_changes_before_approval')} ({updatedSuppliers.length} {t('common.suppliers')})
          </p>
          <div className="preview-badges">
            <span className="preview-badge">{t('common.preview_mode')}</span>
            <span className="changes-badge">{t('common.changes_applied')}</span>
          </div>
        </div>

        <div className="preview-container">
          <div className="preview-table-wrapper">
            <table className="preview-table">
              <thead>
                <tr>
                  <th className="metric-column">{t('common.metrics')}</th>
                  {updatedSuppliers.map((supplier: any, index: number) => (
                    <th key={supplier.supplier_id || supplier.id || index} className="supplier-column">
                      <div className="supplier-header">
                        <span className="supplier-name">{supplier.supplier_name || supplier.name}</span>
                        <span className="supplier-status">Updated</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((metric, index) => (
                  <tr key={String(metric.key)} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                    <td className="metric-label">{metric.label}</td>
                    {updatedSuppliers.map((supplier: any, supplierIndex: number) => (
                      <td key={`${supplier.supplier_id || supplier.id || supplierIndex}-${String(metric.key)}`} className="metric-value">
                        <div className="metric-content">
                          {metric.type === 'documents' ? (
                            <div className="documents-cell">
                              {metric.getValue(supplier).length > 0 ? (
                                <div className="documents-list">
                                  {metric.getValue(supplier).map((doc: any, docIndex: number) => (
                                    <div key={docIndex} className="document-item">
                                      <span className="document-name">
                                        📄 {doc.original_filename || doc.filename || doc.original_name || doc.name}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="no-documents">No documents</span>
                              )}
                            </div>
                          ) : (
                            metric.getValue(supplier)
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="preview-modal-footer">
          <button className="preview-modal-btn secondary" onClick={onBackToEdit}>
            {t('common.back_to_edit')}
          </button>
          <button className="preview-modal-btn secondary" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button 
            className="preview-modal-btn primary"
            onClick={onApproveRequest}
          >
            {t('common.approve_request')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;

